import {
  ExperienceAnchor,
  IItemTemplateTimingDataChangeTask,
  IItemTemplateTimingFixedTask,
  IItemTemplateTimingTriggeredTask,
  ITaskTemplate,
  ITimelineItem,
  UserField
} from '@dadohr/shared/domainTypes/experience';
import { Button, FormControlLabel, Radio } from '@material-ui/core';
import { Calendar } from 'lucide-react';
import moment from 'moment-timezone';
import { ReactNode, useMemo } from 'react';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { SwitchWithLabel } from '../../../../../../components/SwitchWithLabel';
import * as UX from '../../newux/kit';
import { useUnsafeTranslation } from '../../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../layout/Flex';
import { useFeatureEnabled } from '../../../../../../services/features';
import { LightMdToJsx } from '../../../../../../services/formatter';
import { ONE_DAY, toWeekOrDayDuration } from '../../../../../../services/time';
import { TriggeredItemsList } from '../../../TriggeredItemsList';
import { UserTimezoneHandlingInfoBox } from '../../../UserTimezoneHandlongInfoBox';
import { UserTimezoneHandlingPicker } from '../../../UserTimezoneHnadlingPicker';
import {
  getTimelineStartTimeFromTaskBuilderContext,
  TaskBuilderContext
} from '../types';
import { RuleRenderer, RuleBuilderConfig } from '../../../RuleBuilder';
import { SettingsSection, SideContainer } from './layout';
import { SideContainerHeader } from './SideContainerHeader';
import { TaskSettingsOverviewSection } from './TaskSettingsOverviewSection';
import { FixedTimingSummary } from './FixedTimingSummary';
import { TimelineItemTiming } from './TimelineItemTiming';
import { DaySelector, Duration, TimePicker } from './TimeSelectors';
import { TriggerList } from './TriggerList';

const ICON = Calendar;

const TaskTimingInner = ({
  value,
  initialUserTimezoneStartTime,
  allTasks
}: {
  value: ITaskTemplate;
  initialUserTimezoneStartTime: {
    hour: number;
    minute: number;
  } | null;
  allTasks: ITaskTemplate[];
}) => {
  const { timing, preferUserTimezone } = value;
  const { t } = useUnsafeTranslation();

  const userTimezoneHandlingEnabled = useFeatureEnabled(
    'USER_BASED_TIMEZONES_V1'
  );

  if (timing.type === 'fixed') {
    const { start, end, time } = timing;
    const duration = end - start;

    const sT =
      time ||
      (userTimezoneHandlingEnabled ? initialUserTimezoneStartTime : null);
    return (
      <>
        <div>
          {t('Starts on day {{ day }}', {
            day: Math.round(start / ONE_DAY) + 1
          })}
          {sT && (
            <span>
              {' ' + t('at') + ' '}
              {moment().hour(sT.hour).minute(sT.minute).format('hh:mm a')}
              {preferUserTimezone && ` ${t(`in user's timezone`)}`}
            </span>
          )}
        </div>
        <LightMdToJsx>
          {t('{{ duration }} deadline', {
            duration: toWeekOrDayDuration(duration, t, {
              forceSingular: true
            })
          })}
        </LightMdToJsx>
      </>
    );
  }

  if (timing.type === 'triggered') {
    return (
      <>
        <TriggerList timing={timing} allTasks={allTasks}>
          <FlexContainer justifyContent="flex-start" spacing={0.5} fullWidth>
            <LightMdToJsx>
              {t('{{ duration }} deadline', {
                duration: toWeekOrDayDuration(timing.end.duration, t, {
                  forceSingular: true
                })
              })}
            </LightMdToJsx>
          </FlexContainer>
        </TriggerList>
      </>
    );
  }

  if (timing.type === 'dataChange') {
    return (
      <>
        <div>
          {timing.watchField
            ? t('Triggered when a data field changes')
            : t('Triggered when conditions are met')}
        </div>
        <LightMdToJsx>
          {t('{{ duration }} deadline', {
            duration: toWeekOrDayDuration(timing.end.duration, t, {
              forceSingular: true
            })
          })}
        </LightMdToJsx>
      </>
    );
  }

  console.warn(`Cannot render timing type - ${timing}`);
  return null;
};

export const TaskSettingsOverviewSectionStartAndDuration = ({
  title,
  value,
  onEdit,
  divider,
  initialUserTimezoneStartTime,
  allTasks
}: {
  title: string;
  value: ITaskTemplate;
  onEdit?: () => void;
  divider?: boolean;
  initialUserTimezoneStartTime: {
    hour: number;
    minute: number;
  } | null;
  allTasks: ITaskTemplate[];
}) => {
  return (
    <TaskSettingsOverviewSection
      icon={ICON}
      title={title}
      onEdit={onEdit}
      divider={divider}
    >
      <TaskTimingInner
        value={value}
        allTasks={allTasks}
        initialUserTimezoneStartTime={initialUserTimezoneStartTime}
      />
    </TaskSettingsOverviewSection>
  );
};

/**
 * NEW_UX read-only timing summary (task viewer, img4/5): "Start" and "Deadline"
 * as two separate labelled sections (no shared "Timing & Triggers" card).
 * Reuses the same humanization as TaskTimingInner, split into start vs deadline.
 */
export const TaskViewerStartAndDeadline = ({
  value,
  initialUserTimezoneStartTime,
  allTasks,
  onGoToOtherItem,
  ruleConfig,
  anchor,
  tz
}: {
  value: ITaskTemplate;
  initialUserTimezoneStartTime: { hour: number; minute: number } | null;
  allTasks: ITaskTemplate[];
  onGoToOtherItem?: (otherItem: ITimelineItem) => void;
  ruleConfig?: RuleBuilderConfig<UserField>;
  anchor: ExperienceAnchor;
  tz: string;
}) => {
  const { t } = useUnsafeTranslation();
  const { timing, preferUserTimezone } = value;
  const userTimezoneHandlingEnabled = useFeatureEnabled(
    'USER_BASED_TIMEZONES_V1'
  );

  let startNode: ReactNode = null;
  let deadlineDuration = 0;

  if (timing.type === 'fixed') {
    const { start, end, time } = timing;
    deadlineDuration = end - start;
    const sT =
      time || (userTimezoneHandlingEnabled ? initialUserTimezoneStartTime : null);
    startNode = (
      <>
        <FixedTimingSummary timing={timing} anchor={anchor} tz={tz} />
        {sT && (
          <span>
            {' ' + t('at') + ' '}
            {moment().hour(sT.hour).minute(sT.minute).format('hh:mm a')}
            {preferUserTimezone && ` ${t(`in user's timezone`)}`}
          </span>
        )}
      </>
    );
  } else if (timing.type === 'triggered') {
    deadlineDuration = timing.end.duration;
    startNode = (
      <TriggerList
        timing={timing}
        allTasks={allTasks}
        onGoToOtherItem={onGoToOtherItem}
      />
    );
  } else if (timing.type === 'dataChange') {
    deadlineDuration = timing.end.duration;
    const day = Math.round((timing.earliestStart ?? 0) / ONE_DAY) + 1;
    const fieldLabel = timing.watchField
      ? ruleConfig?.[timing.watchField]?.label
      : undefined;
    startNode = timing.watchField ? (
      <UX.TimingSummaryText
        text={t('When **{{ field }}** changes, on or after **day {{ day }}** of experience', {
          field: fieldLabel || t('a field'),
          day
        })}
      />
    ) : (
      <div>
        <UX.TimingSummaryText
          text={t('When conditions met, on or after **day {{ day }}** of experience:', {
            day
          })}
        />
        {ruleConfig && (
          <div style={{ marginTop: 4 }}>
            <RuleRenderer value={timing.rules ?? null} config={ruleConfig} variant="oneline" />
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <UX.FieldLabel>{t('Start')}</UX.FieldLabel>
        <div
          style={{
            color: UX.tokens.color.grey700,
            fontSize: 16,
            fontWeight: UX.tokens.font.weightScale.regular
          }}
        >
          {startNode}
        </div>
        {value.catchup?.disabled && (
          <div
            style={{
              marginTop: 4,
              fontStyle: 'italic',
              color: UX.tokens.color.grey500
            }}
          >
            {t(`Won't send in catch-up mode`)}
          </div>
        )}
      </div>
      <div style={{ marginBottom: 16 }}>
        <UX.FieldLabel>{t('Deadline')}</UX.FieldLabel>
        <div
          style={{
            color: UX.tokens.color.grey700,
            fontSize: 16,
            fontWeight: UX.tokens.font.weightScale.regular
          }}
        >
          {t('{{ duration }} after task start', {
            duration: toWeekOrDayDuration(deadlineDuration, t)
          })}
        </div>
      </div>
    </>
  );
};

// NEW_UX: section labels match the kit (same font as Reminders); classic keeps
// CustomInputLabel. Defined at module scope so
// it keeps a stable identity across renders — a component redefined on every
// render is treated by React as a different component type each time, forcing
// everything inside it (here, the day/time inputs) to unmount and remount on
// every keystroke, which drops focus.
const StartAndDurationLabelWrap = ({
  label,
  newUx,
  children
}: {
  label: string;
  fullWidth?: boolean;
  newUx?: boolean;
  children: ReactNode;
}) =>
  newUx ? (
    <div style={{ width: '100%' }}>
      <UX.SectionLabel style={{ marginTop: 0 }}>{label}</UX.SectionLabel>
      {children}
    </div>
  ) : (
    <CustomInputLabel label={label} fullWidth>
      {children}
    </CustomInputLabel>
  );

export const TaskSettingsStartAndDurationV2 = ({
  title,
  description,
  onBack,

  value,
  onChange,
  expTpl,
  triggeredItems,
  onGoToOtherItem,
  newUx = false
}: {
  title: string;
  description: string;
  onBack: () => void;

  value: ITaskTemplate;
  onChange: React.Dispatch<React.SetStateAction<ITaskTemplate>>;
  expTpl: TaskBuilderContext;
  triggeredItems: ITimelineItem[];
  onGoToOtherItem?: (item: ITimelineItem) => void;
  newUx?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const optionIdsToIgnore = useMemo(() => {
    return [value.id];
  }, [value.id]);

  const body = (
    <SettingsSection>
      <FlexContainerVertical spacing={3}>
          <StartAndDurationLabelWrap label={t('Start')} newUx={newUx} fullWidth>
            <TimelineItemTiming
              value={value.timing}
              onChange={(nextTiming) => {
                onChange((v) => {
                  if (nextTiming.type === 'fixed') {
                    const end: IItemTemplateTimingFixedTask['end'] =
                      v.timing.type === 'fixed'
                        ? nextTiming.start + (v.timing.end - v.timing.start)
                        : v.timing.end.duration;
                    return {
                      ...v,
                      timing: {
                        ...nextTiming,
                        end
                      }
                    };
                  }
                  if (nextTiming.type === 'triggered') {
                    const end: IItemTemplateTimingTriggeredTask['end'] =
                      v.timing.type === 'triggered'
                        ? v.timing.end
                        : v.timing.type === 'fixed'
                        ? {
                            type: 'fixedDuration',
                            duration: v.timing.end - v.timing.start
                          }
                        : {
                            type: 'fixedDuration',
                            duration: v.timing.end.duration
                          };
                    return {
                      ...v,
                      timing: {
                        ...nextTiming,
                        end
                      }
                    };
                  }
                  if (nextTiming.type === 'dataChange') {
                    const duration: number =
                      v.timing.type === 'dataChange'
                        ? v.timing.end.duration
                        : v.timing.type === 'fixed'
                        ? v.timing.end - v.timing.start
                        : v.timing.end.duration;
                    const end: IItemTemplateTimingDataChangeTask['end'] = {
                      type: 'fixedDuration',
                      duration
                    };
                    return {
                      ...v,
                      timing: {
                        ...nextTiming,
                        end
                      }
                    };
                  }

                  return v;
                });
              }}
              tplContext={expTpl}
              optionIdsToIgnore={optionIdsToIgnore}
              onGoToOtherItem={onGoToOtherItem}
              newUx={newUx}
            />
          </StartAndDurationLabelWrap>
          <StartAndDurationLabelWrap label={t('Deadline')} newUx={newUx} fullWidth>
            {value.timing.type === 'fixed' && (
              <Duration
                value={value.timing.end - value.timing.start}
                onChange={(nextDuration) => {
                  onChange((v) => {
                    if (v.timing.type !== 'fixed') {
                      // should never happen
                      return v;
                    }
                    return {
                      ...v,
                      timing: {
                        ...v.timing,
                        end: v.timing.start + nextDuration
                      }
                    };
                  });
                  return null;
                }}
                suffix={t('after task start')}
              />
            )}
            {value.timing.type === 'dataChange' && (
              <Duration
                value={value.timing.end.duration}
                onChange={(nextDuration) => {
                  onChange((v) => {
                    if (v.timing.type !== 'dataChange') {
                      return v;
                    }
                    return {
                      ...v,
                      timing: {
                        ...v.timing,
                        end: {
                          ...v.timing.end,
                          duration: nextDuration
                        }
                      }
                    };
                  });
                }}
                suffix={t('after task start')}
              />
            )}
            {value.timing.type === 'triggered' && (
              <FlexContainerVertical spacing={1}>
                <Duration
                  value={value.timing.end.duration}
                  onChange={(nextDuration) => {
                    onChange((v) => {
                      if (v.timing.type !== 'triggered') {
                        // should never happen
                        return v;
                      }
                      return {
                        ...v,
                        timing: {
                          ...v.timing,
                          end: {
                            ...v.timing.end,
                            duration: nextDuration
                          }
                        }
                      };
                    });
                  }}
                />
                <FormControlLabel
                  control={
                    <Radio
                      checked={value.timing.end.type === 'fixedDuration'}
                      onChange={() => {
                        onChange((v) => {
                          if (v.timing.type !== 'triggered') {
                            // should never happen
                            return v;
                          }
                          return {
                            ...v,
                            timing: {
                              ...v.timing,
                              end: {
                                ...v.timing.end,
                                type: 'fixedDuration'
                              }
                            }
                          };
                        });
                      }}
                      color="primary"
                    />
                  }
                  label={t('after the start of this task')}
                />
                <FormControlLabel
                  control={
                    <Radio
                      checked={
                        value.timing.end.type === 'relativeToDeadlineOfTrigger'
                      }
                      onChange={() =>
                        onChange((v) => {
                          if (v.timing.type !== 'triggered') {
                            // should never happen
                            return v;
                          }
                          return {
                            ...v,
                            timing: {
                              ...v.timing,
                              end: {
                                ...v.timing.end,
                                type: 'relativeToDeadlineOfTrigger'
                              }
                            }
                          };
                        })
                      }
                      color="primary"
                    />
                  }
                  label={t('after the deadline for the preceeding task')}
                />
              </FlexContainerVertical>
            )}
          </StartAndDurationLabelWrap>

          {value.timing.type === 'fixed' && (
            <StartAndDurationLabelWrap label={t('Catch-up mode')} newUx={newUx} fullWidth>
              {newUx ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <UX.Toggle
                    on={value.catchup.disabled}
                    ariaLabel={t(`Don't send this item in catch-up mode`)}
                    onChange={(nextChecked) =>
                      onChange((v) => ({
                        ...v,
                        catchup: { ...v.catchup, disabled: nextChecked }
                      }))
                    }
                  />
                  <span style={{ fontSize: 13 }}>
                    {t(`Don't send this item in catch-up mode`)}
                  </span>
                </div>
              ) : (
                <SwitchWithLabel
                  label={t(`Don't send this item in catch-up mode`)}
                  checked={value.catchup.disabled}
                  onChange={(nextChecked) =>
                    onChange((v) => ({
                      ...v,
                      catchup: {
                        ...v.catchup,
                        disabled: nextChecked
                      }
                    }))
                  }
                />
              )}
            </StartAndDurationLabelWrap>
          )}
          {!!triggeredItems.length && (
            <FlexContainerVertical>
              <div>
                {t(
                  `Changing this task’s start or deadline will also affect the following triggered items. To disconnect them, edit the start settings for each item.`
                )}
              </div>
              <TriggeredItemsList
                triggeredItems={triggeredItems}
                onGoToOtherItem={onGoToOtherItem}
                tplContext={expTpl}
              />
            </FlexContainerVertical>
          )}
        </FlexContainerVertical>
      </SettingsSection>
  );
  // NEW_UX: render bare so the Timing panel can stack Start/Deadline/Catch-up/Triggered
  // and Reminders in one scroll (the shell owns the panel header). Classic path keeps
  // the SideContainer + header.
  return newUx ? (
    body
  ) : (
    <SideContainer>
      <SideContainerHeader
        def={{
          label: title,
          description,
          icon: ICON
        }}
        onBack={onBack}
      />
      {body}
    </SideContainer>
  );
};

export const TaskSettingsStartAndDuration = ({
  title,
  description,
  onBack,

  value,
  onChange,
  expTpl
}: {
  title: string;
  description: string;
  onBack: () => void;

  value: ITaskTemplate;
  onChange: React.Dispatch<React.SetStateAction<ITaskTemplate>>;
  expTpl: TaskBuilderContext;
}) => {
  const userTimezoneHandlingEnabled = useFeatureEnabled(
    'USER_BASED_TIMEZONES_V1'
  );
  const { t } = useUnsafeTranslation();

  const {
    onChangeStart,
    onChangeDuration,
    onChangeStartTime,
    onClearStartTime,
    onSetDefaultStartTime,
    onChangePreferUserTimezone
  } = useMemo(() => {
    return {
      onChangeStart: (nextStart: number) =>
        onChange((v) => {
          if (v.timing.type !== 'fixed') {
            return v;
          }
          const duration = v.timing.end - v.timing.start;
          return {
            ...v,
            timing: {
              ...v.timing,
              start: nextStart,
              end: nextStart + duration
            }
          };
        }),
      onChangeDuration: (nextDuration: number) =>
        onChange((v) => {
          if (v.timing.type !== 'fixed') {
            return v;
          }
          return {
            ...v,
            timing: {
              ...v.timing,
              end: v.timing.start + nextDuration
            }
          };
        }),
      onChangeStartTime: (startTime: { hour: number; minute: number }) =>
        onChange((v) => {
          if (v.timing.type !== 'fixed') {
            return v;
          }
          return {
            ...v,
            timing: {
              ...v.timing,
              time: startTime
            }
          };
        }),
      onSetDefaultStartTime: () =>
        onChange((v) => {
          if (v.timing.type !== 'fixed') {
            return v;
          }
          return {
            ...v,
            timing: { ...v.timing, time: { hour: 9, minute: 0 } }
          };
        }),
      onClearStartTime: () => {
        onChange((v) => {
          if (v.timing.type !== 'fixed') {
            return v;
          }
          return {
            ...v,
            timing: { ...v.timing, time: null }
          };
        });
      },
      onChangePreferUserTimezone: (nextValue: boolean) =>
        onChange((v) => ({ ...v, preferUserTimezone: nextValue }))
    };
  }, [onChange]);

  return (
    <SideContainer>
      <SideContainerHeader
        def={{
          label: title,
          description,
          icon: ICON
        }}
        onBack={onBack}
      />
      {value.timing.type === 'fixed' && (
        <SettingsSection>
          <FormRow>
            <CustomInputLabel
              label={t('Start')}
              info={
                !userTimezoneHandlingEnabled
                  ? t(
                      'Task without a specified time will start at the same time as when the experience started'
                    )
                  : undefined
              }
              fullWidth
            >
              <DaySelector
                value={value.timing.start}
                onChange={onChangeStart}
              />
              {!userTimezoneHandlingEnabled && (
                <>
                  {!value.timing.time && (
                    <Button
                      color="primary"
                      onClick={onSetDefaultStartTime}
                      size="small"
                    >
                      {t('Add Time')}
                    </Button>
                  )}
                  {value.timing.time && (
                    <TimePicker
                      t={value.timing.time}
                      minuteStep={10}
                      onChange={onChangeStartTime}
                      onClear={onClearStartTime}
                    />
                  )}
                </>
              )}
            </CustomInputLabel>
          </FormRow>

          {userTimezoneHandlingEnabled && (
            <>
              <FormRow>
                <TimePicker
                  t={
                    value.timing.time ??
                    getTimelineStartTimeFromTaskBuilderContext(expTpl) ?? {
                      hour: 9,
                      minute: 0
                    }
                  }
                  minuteStep={10}
                  onChange={onChangeStartTime}
                />
                <UserTimezoneHandlingPicker
                  value={value.preferUserTimezone}
                  onChange={onChangePreferUserTimezone}
                  defaultTz={expTpl.tz}
                  userLabel={t('user')}
                />
              </FormRow>
              {value.preferUserTimezone && (
                <FormRow>
                  <UserTimezoneHandlingInfoBox tz={expTpl.tz} />
                </FormRow>
              )}
            </>
          )}

          <FormRow>
            <CustomInputLabel label={t('Duration')} fullWidth>
              <Duration
                value={value.timing.end - value.timing.start}
                onChange={onChangeDuration}
              />
            </CustomInputLabel>
          </FormRow>
        </SettingsSection>
      )}
    </SideContainer>
  );
};
